/**
 * UserModal Component
 *
 * Modal dialog for creating and editing tenant users with TAP-only authentication.
 * TAP (Temporary Access Pass) is the only authentication method.
 * Handles form validation, save confirmation, and unsaved changes warning.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { UserForm } from '../UserForm';
import { EditUserPanel, EditUserChanges } from './EditUserPanel';
import { useTenantContext } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import type { TenantUser } from '../../../types/user';
import { UserApiService } from '../../../services/userApi';
import { transferUserOrganization } from '../../../services/userAdminApi';

/** Tenant info for organization view tenant access tab */
interface OrganizationTenant {
  tenant_id: string;
  tenant_name: string;
}

/** Available organization for transfer dropdown */
interface AvailableOrganization {
  id: number;
  name: string;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: Partial<TenantUser>) => Promise<void>;
  mode: 'add' | 'edit';
  tenantId: string;
  existingUser?: TenantUser;
  /** Organization tenants for tenant access tab (organization view only) */
  organizationTenants?: OrganizationTenant[];
  /** Organization ID for tenant access updates */
  organizationId?: number;
  /** Callback for saving tenant access changes */
  onSaveTenantAccess?: (organizationId: number, userId: number, tenantIds: string[]) => Promise<void>;
  /** Available organizations for transfer (global_admin only) */
  availableOrganizations?: AvailableOrganization[];
  /** Whether viewing users from the home tenant (excludes Tenant User role) */
  isHomeTenant?: boolean;
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  tenantId,
  existingUser,
  organizationTenants = [],
  organizationId,
  onSaveTenantAccess,
  availableOrganizations = [],
  isHomeTenant = false,
}) => {
  const { currentTenant, tenantList } = useTenantContext();
  const { user: currentUser } = useAuth();
  // When tenantId prop is provided, look up domain from tenant list (org detail view)
  // Otherwise fall back to currentTenant.domain (global admin view with tenant dropdown)
  const tenantDomain = tenantId
    ? tenantList.find((t) => t.tenant_id === tenantId)?.domain || ''
    : currentTenant?.domain || '';

  // Shared state
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{ title: string; message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Add mode state (UserForm)
  const [isValid, setIsValid] = useState(mode === 'edit'); // Edit mode starts valid
  const [_formValidationErrors, setFormErrors] = useState<string[]>([]);
  const [currentUserData, setCurrentUserData] = useState<Partial<Record<string, unknown>>>({});
  const [hasAddModeChanges, setHasAddModeChanges] = useState(false);

  // Edit mode state (EditUserPanel)
  const [hasEditModeChanges, setHasEditModeChanges] = useState(false);

  // Combined hasChanges
  const hasChanges = mode === 'add' ? hasAddModeChanges : hasEditModeChanges;

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      // Escape key to close
      if (event.key === 'Escape') {
        handleClose();
      }

      // Ctrl+S or Cmd+S to save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (isValid && !isSaving) {
          handleSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isValid, isSaving, hasChanges]);

  // Add mode handlers (UserForm)
  const handleFormValidationChange = (valid: boolean, errors: string[]) => {
    setIsValid(valid);
    setFormErrors(errors);
  };

  const handleFormChange = (data: Partial<Record<string, unknown>>, changed: boolean) => {
    setCurrentUserData(data);
    setHasAddModeChanges(changed);
  };

  // Edit mode handler (EditUserPanel)
  // Store pending changes for save
  const pendingEditChangesRef = useRef<EditUserChanges | null>(null);

  const handleEditPanelSave = async (changes: EditUserChanges) => {
    pendingEditChangesRef.current = changes;
    await handleSaveEditMode(changes);
  };

  const handleEditPanelCancel = () => {
    handleClose();
  };

  // Track changes from EditUserPanel
  const handleEditPanelChangesUpdate = (hasChanges: boolean) => {
    setHasEditModeChanges(hasChanges);
  };

  const handleClose = () => {
    if (hasChanges && !isSaving) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
    // Restore focus to main dialog after confirmation dialog closes
    setTimeout(() => {
      if (dialogRef.current) {
        dialogRef.current.focus();
      }
    }, 100);
  };

  // Parse API error messages into user-friendly format
  const parseApiError = (error: unknown): { title: string; message: string } => {
    const rawMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    // Try to extract Graph API error details
    // Format: "Graph API error 400: {"error":{"code":"...","message":"..."}}"
    const graphApiMatch = rawMessage.match(/Graph API error \d+: (.+)/);
    if (graphApiMatch) {
      try {
        const errorJson = JSON.parse(graphApiMatch[1]);
        const code = errorJson.error?.code || 'Error';
        const message = errorJson.error?.message || rawMessage;

        // Map common error codes to friendly titles
        const titleMap: Record<string, string> = {
          'Request_BadRequest': 'Invalid Request',
          'ObjectConflict': 'Conflict',
          'Authorization_RequestDenied': 'Permission Denied',
          'Request_ResourceNotFound': 'Not Found',
        };

        return {
          title: titleMap[code] || 'Error',
          message: message,
        };
      } catch {
        // JSON parse failed, use raw message
      }
    }

    // Check for common error patterns
    if (rawMessage.includes('userPrincipalName already exists')) {
      return {
        title: 'User Already Exists',
        message: 'A user with this username already exists. Please choose a different username.',
      };
    }

    if (rawMessage.includes('401') || rawMessage.includes('Unauthorized')) {
      return {
        title: 'Authentication Error',
        message: 'Your session may have expired. Please try again or sign in again.',
      };
    }

    if (rawMessage.includes('403') || rawMessage.includes('Forbidden')) {
      return {
        title: 'Permission Denied',
        message: 'You do not have permission to perform this action.',
      };
    }

    return {
      title: 'Failed to Save User',
      message: rawMessage,
    };
  };

  // Save handler for edit mode (EditUserPanel)
  const handleSaveEditMode = async (changes: EditUserChanges) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Transform EditUserChanges to backend format
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedData: any = {};

      // Include display name only if changed
      if (changes.displayName !== undefined) {
        transformedData.display_name = changes.displayName;
      }

      // Include account enabled only if changed
      if (changes.accountEnabled !== undefined) {
        transformedData.account_enabled = changes.accountEnabled;
      }

      // Include role change
      if (changes.roleChange) {
        transformedData.role = changes.roleChange.to;
      }

      // Track whether any changes require a user list refresh
      let needsRefresh = false;

      // Save user data if there are any changes
      if (Object.keys(transformedData).length > 0) {
        await onSave(transformedData);
        needsRefresh = true;
      }

      // Handle license changes
      if (existingUser && (changes.licensesToAdd.length > 0 || changes.licensesToRemove.length > 0)) {
        await UserApiService.manageLicenses(tenantId, existingUser.id, {
          add_licenses: changes.licensesToAdd,
          remove_licenses: changes.licensesToRemove,
        });
        needsRefresh = true;
      }

      // Handle tenant access changes
      if (changes.tenantAccessToAdd.length > 0 || changes.tenantAccessToRemove.length > 0) {
        if (onSaveTenantAccess && organizationId && existingUser?.local_user_id) {
          // Calculate new tenant IDs from changes
          const currentTenantIds = existingUser?.accessible_tenant_ids || [];
          const newTenantIds = [
            ...currentTenantIds.filter(id => !changes.tenantAccessToRemove.includes(id)),
            ...changes.tenantAccessToAdd,
          ];
          await onSaveTenantAccess(organizationId, existingUser.local_user_id, newTenantIds);
          needsRefresh = true;
        }
      }

      // Handle organization transfer
      if (changes.organizationChange && existingUser?.local_user_id) {
        await transferUserOrganization(existingUser.local_user_id, changes.organizationChange.to);
        UserApiService.invalidateCache();
        needsRefresh = true;
      }

      // Trigger refresh if any operation was performed but onSave wasn't called yet
      if (needsRefresh && Object.keys(transformedData).length === 0) {
        await onSave({});
      }

      // Success - close the modal
      onClose();
    } catch (error) {
      // Parse error and show in error dialog
      const parsedError = parseApiError(error);
      setErrorInfo(parsedError);
      setShowErrorDialog(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Save handler for add mode (UserForm)
  const handleSave = async () => {
    if (!isValid || isSaving) return;

    setIsSaving(true);
    try {
      // Transform form data for backend (snake_case field names)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formData = currentUserData as any;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedData: any = {
        display_name: formData.displayName,
        account_enabled: formData.accountEnabled,
      };

      // For create mode, construct UPN and mail nickname
      if (formData.upnPrefix) {
        transformedData.user_principal_name = `${formData.upnPrefix}@${tenantDomain}`;
        transformedData.mail_nickname = formData.upnPrefix;

        // Always include TAP fields (TAP is the only authentication method)
        transformedData.use_tap = true;
        transformedData.tap_lifetime_minutes = formData.tap_lifetime_minutes || 60;
        transformedData.tap_one_time_use = formData.tap_one_time_use !== undefined ? formData.tap_one_time_use : true;

        // Always include mobile phone (REQUIRED for TAP delivery)
        if (formData.mobilePhone) {
          transformedData.mobile_phone = formData.mobilePhone;
        }

        // Force SMS delivery for TAP (mobile phone is required, so always send via SMS)
        transformedData.send_via_sms = true;

        // Include email communication preference (optional)
        transformedData.send_via_email = formData.sendViaEmail || false;

        // Include personal email if email checkbox is checked
        if (formData.sendViaEmail && formData.personalEmail) {
          transformedData.personal_email = formData.personalEmail;
        }
      }

      await onSave(transformedData);

      // Success - close the modal
      onClose();
    } catch (error) {
      // Parse error and show in error dialog
      const parsedError = parseApiError(error);
      setErrorInfo(parsedError);
      setShowErrorDialog(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(_ev, data) => {
          if (!data.open) {
            handleClose();
          }
        }}
      >
        <DialogSurface data-testid="user-modal" style={{ maxWidth: '750px', width: '90vw' }} ref={dialogRef} tabIndex={-1}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={handleClose}
                  disabled={isSaving}
                />
              }
            >
              {mode === 'add' ? 'Add New User' : 'Edit User'}
            </DialogTitle>

            <DialogContent>
              {/* Edit mode - single panel with EditUserPanel */}
              {mode === 'edit' && existingUser && (
                <EditUserPanel
                  user={existingUser}
                  tenantId={tenantId}
                  onSave={handleEditPanelSave}
                  onCancel={handleEditPanelCancel}
                  canEditLicenses={true}
                  canEditRole={currentUser?.role === 'global_admin' || currentUser?.role === 'organization_owner'}
                  canEditTenantAccess={organizationTenants.length > 0}
                  canEditOrganization={currentUser?.role === 'global_admin' && availableOrganizations.length > 0}
                  organizationTenants={organizationTenants}
                  availableOrganizations={availableOrganizations}
                  organizationId={organizationId}
                  currentUserRole={currentUser?.role}
                  onChangesUpdate={handleEditPanelChangesUpdate}
                  isSaving={isSaving}
                  isHomeTenant={isHomeTenant}
                />
              )}

              {/* Add mode - UserForm */}
              {mode === 'add' && (
                <>
                  <UserForm
                    mode={mode}
                    initialData={existingUser}
                    tenantDomain={tenantDomain}
                    onValidationChange={handleFormValidationChange}
                    onChange={handleFormChange}
                    disabled={isSaving}
                    currentUserRole={currentUser?.role}
                    isOrganizationContext={organizationTenants.length > 0}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                    <Button
                      appearance="secondary"
                      onClick={handleClose}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <AsyncButton
                      appearance="primary"
                      onClick={handleSave}
                      disabled={!isValid}
                      loading={isSaving}
                      loadingText="Saving..."
                    >
                      Save
                    </AsyncButton>
                  </div>
                </>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirmation dialog for unsaved changes */}
      <Dialog
        open={showConfirmDialog}
        onOpenChange={(_ev, data) => {
          if (!data.open) {
            setShowConfirmDialog(false);
          }
        }}
      >
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogContent>
              You have unsaved changes. Are you sure you want to close without saving?
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleCancelClose}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleConfirmClose}>
                Discard
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Error dialog */}
      <Dialog
        open={showErrorDialog}
        onOpenChange={(_ev, data) => {
          if (!data.open) {
            setShowErrorDialog(false);
            setErrorInfo(null);
          }
        }}
      >
        <DialogSurface style={{ maxWidth: '450px' }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={() => {
                    setShowErrorDialog(false);
                    setErrorInfo(null);
                  }}
                />
              }
            >
              {errorInfo?.title || 'Error'}
            </DialogTitle>
            <DialogContent>
              <p style={{ margin: 0, lineHeight: '1.5' }}>
                {errorInfo?.message}
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  setShowErrorDialog(false);
                  setErrorInfo(null);
                }}
              >
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
